import {FC} from 'react'

import {Section} from '@/components/common/content/Section'
import {SectionLayout} from '@/components/common/content/SectionLayout'
import {usePreferences} from '@/components/context/Preferences'
import {getContactContent} from '@/content/contact'
import {getCountryCards} from '@/content/countryCards'
import {useHomepageContent} from '@/content/homepage'
import {Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {validateRegion} from '@/utils/countries'
import {Jurisdiction} from '@/utils/countries/constants'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {trackClickOnContactEmail} from '@/utils/tracking/helpers'

import {CountryFlag} from './CountryFlag'

type CountriesOfferProps = {
  title?: string
  description: string
  country?: Jurisdiction
  trackedEventParameters: any
}

export const CountriesOffer: FC<React.PropsWithChildren<CountriesOfferProps>> = ({
  title,
  description,
  country,
  trackedEventParameters,
}) => {
  const language = useLanguage()
  const {t} = useTranslation('page')

  const {origin, destination} = usePreferences()
  const {countriesOffer} = useHomepageContent()
  const {emailAddress, emailAddressHref} = getContactContent()
  const countryCards = getCountryCards(language, origin, validateRegion(country) || destination)

  const trackVerificationButtonClick = () => analytics.track('VerificationCountry')

  return (
    <>
      <Section hasPaddingTop>
        <SectionLayout
          title={{text: title || t(countriesOffer.title)}}
          description={{
            text: (
              <Trans t={t} i18nKey={description} ns="common">
                text
                <a
                  className="link"
                  href={emailAddressHref}
                  onClick={() => trackClickOnContactEmail(trackedEventParameters)}
                >
                  {emailAddress}
                </a>
                text
              </Trans>
            ),
          }}
          align="left"
          orientation="horizontal"
          maxWidth="570px"
        >
          <ul className="list">
            {countryCards.map(({key, imageSrc, imageAlt}, i) => {
              const isHighlighted = countriesOffer.flags[i]?.isHighlighted
              const position = countriesOffer.flags[i].position
              const link = (country !== key
                ? {pathname: routeTo.landingPage(key), query: {}}
                : undefined) || {
                pathname: routeTo.verification.form(),
                query: {
                  country: key.toLowerCase(),
                  ...(country ? {countrySpecific: country.toLowerCase()} : {}),
                },
              }
              return (
                <CountryFlag
                  key={key}
                  {...{imageSrc, imageAlt, position, isHighlighted}}
                  link={link}
                  onClick={trackVerificationButtonClick}
                />
              )
            })}
          </ul>
        </SectionLayout>
      </Section>

      <style jsx>{`
        .link {
          color: inherit;
          text-decoration: underline;
        }

        .list {
          position: relative;
          width: 100%;
          max-width: 370px;
          height: 265px;
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .list {
            margin-top: ${theme.spacing[5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            max-width: 300px;
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
